"use client"

import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { CustomFilterIcon } from "@/components/custom-filter-icon"
import { CustomColumnsIcon } from '@/components/custom-columns-icon'
import type { WorkflowStage } from "@/types/workflow"
import { useTranslation } from '@/hooks/use-translation'
import React from "react"
import { Pin, ChevronDown } from 'lucide-react'

interface GridHeaderProps {
  stage: WorkflowStage
  stageConfig?: any // Add stage configuration data
  language?: string // Add language parameter for localization
  totalRecords: number
  selectedCount: number
  hasActiveFilters: boolean
  onEditColumns: () => void
  onToggleFilter: () => void
  onClearFilters: () => void
  onDeselectAll: () => void
  showFilterButton?: boolean
  filterCount?: number
  isFilterActive?: boolean
  isLoadingHeader?: boolean
  onBulkEdit?: () => void
  onMoveTo?: () => void // <-- add this
  // Optional pin dock to support drag-drop pinning/unpinning
  showPinDock?: boolean
  onPinDockDrop?: () => void
  onPinDockClick?: () => void
  showEditColumnsButton?: boolean
}

export const GridHeader = React.forwardRef<HTMLDivElement, GridHeaderProps>(function GridHeader({
  stage,
  stageConfig,
  language = 'en',
  totalRecords,
  selectedCount,
  hasActiveFilters,
  onEditColumns,
  onToggleFilter,
  onClearFilters,
  onDeselectAll,
  showFilterButton = true,
  filterCount = 0,
  isFilterActive = false,
  isLoadingHeader = false,
  onBulkEdit,
  onMoveTo,
  showPinDock,
  onPinDockDrop,
  onPinDockClick,
  showEditColumnsButton = true,
}: GridHeaderProps, ref) {
  const { t } = useTranslation();

  return (
    <div
      ref={ref}
      style={{
        background: '#F9F9FF',
        borderRadius: 16,
        padding: '18px 32px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 1px 4px 0 rgba(0,0,0,0.02)',
        border: 'solid #E7E9ED 1px'
      }}
    >
      {selectedCount > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#0F214B', fontWeight: 400, fontSize: 16 }}>
            {selectedCount} {t('itemsSelected')}
          </span>
          <button
            onClick={onDeselectAll}
            style={{
              background: 'none',
              border: 'none',
              color: '#0F214B',
              fontSize: 18,
              cursor: 'pointer',
              marginRight: 4,
            }}
            aria-label={t('clearSelection')}
          >
            ×
          </button>
          {/* Dropdown menu for actions */}
          <div style={{ minWidth: 140 }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  style={{
                    border: '1px solid #E0E3EB',
                    borderRadius: 12,
                    padding: '8px 18px',
                    fontSize: 16,
                    background: '#fff',
                    color: '#2B345E',
                    fontWeight: 400,
                    outline: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    minWidth: 140,
                  }}
                >
                  {t('selectAction') || 'Select action'}
                  <ChevronDown size={16} style={{ marginLeft: 8, opacity: 0.7 }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="start" 
                style={{ 
                  minWidth: 140,
                  background: '#fff',
                  border: '1px solid #E0E3EB',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
              >
                <DropdownMenuItem 
                  onClick={() => onBulkEdit && onBulkEdit()}
                  style={{
                    fontSize: 16,
                    color: '#2B345E',
                    padding: '8px 12px',
                    cursor: 'pointer'
                  }}
                >
                  {t('edit') || 'Edit'}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onMoveTo && onMoveTo()}
                  style={{
                    fontSize: 16,
                    color: '#2B345E', 
                    padding: '8px 12px',
                    cursor: 'pointer'
                  }}
                >
                  {t('moveTo') || 'Move to'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isLoadingHeader ? (
            <span
              className="animate-pulse"
              style={{
                display: 'inline-block',
                width: 180,
                height: 28,
                borderRadius: 6,
                background: 'linear-gradient(90deg, #E6E8F3 25%, #BFC6D7 50%, #E6E8F3 75%)',
                backgroundColor: '#BFC6D7',
              }}
            />
          ) : (
            <span style={{ fontWeight: 700, fontSize: 18, color: '#0F214B' }}>
              {stageConfig ? (language === 'fr' ? stageConfig.titleFrench : stageConfig.titleEnglish) : t(stage.toLowerCase() as any)}
              <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 16, color: '#9795B5' }}>({totalRecords})</span>
            </span>
          )}
        </div>
      )}
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Pin dock: default source when no column is pinned, and drop target to unpin */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  role="button"
                  aria-label="Pin dock"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    border: '1px dashed #BFC6D7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: showPinDock ? '#fff' : 'transparent',
                    opacity: showPinDock ? 1 : 0.5,
                    cursor: 'grab'
                  }}
                  onClick={() => onPinDockClick && onPinDockClick()}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (onPinDockDrop) onPinDockDrop();
                  }}
                  draggable={showPinDock}
                  onDragStart={(e) => {
                    try {
                      e.dataTransfer.setData('application/x-workflow-pin', 'pin');
                    } catch {}
                  }}
                >
                  <Pin size={16} className="text-[#0F214B]" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('pinTooltip' as any)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {showEditColumnsButton && (
            <Button variant="outline" className="rounded-full border-[#BFC6D7] text-[#0F214B] font-normal px-5 py-2 flex items-center gap-2" onClick={onEditColumns}>
              <CustomColumnsIcon size={24} color="#0F214B" /> {t('editColumns')}
            </Button>
          )}
        {showFilterButton && (
          <Button
            variant="outline"
            className={`rounded-full border-[#BFC6D7] text-[#0F214B] font-normal px-5 py-2 flex items-center gap-2 relative ${isFilterActive ? 'bg-[rgba(15,33,75,0.2)]' : ''}`}
            style={isFilterActive ? { background: 'rgba(15,33,75,0.2)' } : {}}
            onClick={onToggleFilter}
          >
            <CustomFilterIcon size={24} color="#0F214B" />
            {t('filter')}
            {filterCount > 0 && (
              <span style={{ fontWeight: 500, fontSize: 14, color: '#23213D', marginLeft: 4 }}>
                ({filterCount})
              </span>
            )}
          </Button>
        )}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            style={{
              background: 'none',
              border: 'none',
              color: '#BFC6D7',
              fontWeight: 400,
              fontSize: 16,
              cursor: 'pointer',
              marginLeft: 8,
            }}
            aria-label={t('clearFilters')}
          >
            × {t('clearFilters')}
          </button>
        )}
      </div>
    </div>
  )
})
